import { useCallback, useRef } from "react";
import { Button, useModal } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { useReissueJobMutation } from "~/shared/api/jobs/job.grpc";
import { isJobNotCompleted } from "~/shared/lib/job-status";

/**
 * Putting a Невывоз job on a route. The backend refuses the job itself, so the
 * dispatcher is asked to re-issue it: a new attempt under the same order number
 * (its window today at the missed job's hours, set by the backend) goes on the
 * route instead.
 *
 * `reissueExpiredJobs(jobs)` resolves to `routeIdOf`, which gives the id to put on the
 * route for any job id: the new attempt's for a re-issued Невывоз, the job's own for any
 * other. It resolves to `null` when the dispatcher declined — then nothing was re-issued
 * and the submit stops. Nothing is asked without a Невывоз among the jobs.
 *
 * The backend re-issues an attempt only once, so an attempt made here is kept for the
 * hook's lifetime: a submit that failed after it reuses it instead of asking again.
 */
export function useReissueExpiredJobs() {
  const { openModal, closeModal } = useModal();
  const [reissueJob] = useReissueJobMutation();
  const t = useT();
  const reissued = useRef(new Map<string, ConnectProto.Job>());

  const confirmReissue = useCallback(
    (jobs: ConnectProto.Job[]) =>
      new Promise<boolean>((resolve) => {
        const single = jobs.length === 1;
        const orders = jobs.map((job) => job.orderNumber || `#${job.id}`).join(", ");
        const answer = (yes: boolean) => {
          closeModal(modalId);
          resolve(yes);
        };
        const modalId = openModal({
          size: "sm",
          title: single
            ? t(Namespace.Orders, "reissue.confirmTitle")
            : t(Namespace.Orders, "reissue.confirmTitleMany", { count: jobs.length }),
          body: (
            <span data-testid="reissue-jobs-message">
              {t(Namespace.Orders, single ? "reissue.confirmBody" : "reissue.confirmBodyMany", {
                orders,
              })}
            </span>
          ),
          footer: (
            <div className="flex justify-end gap-2 w-full">
              <Button
                variant="outline"
                onClick={() => answer(false)}
                data-testid="reissue-jobs-dismiss"
              >
                {t(Namespace.Common, "cancel")}
              </Button>
              <Button
                variant="opacityBlueWithOutline"
                onClick={() => answer(true)}
                data-testid="reissue-jobs-confirm"
              >
                {t(Namespace.Orders, single ? "reissue.confirm" : "reissue.confirmMany")}
              </Button>
            </div>
          ),
          onClose: () => resolve(false),
        });
      }),
    [openModal, closeModal, t],
  );

  const reissueExpiredJobs = useCallback(
    async (
      jobs: ConnectProto.Job[],
    ): Promise<((id: bigint | string | number) => bigint) | null> => {
      const expired = jobs.filter((job) => isJobNotCompleted(job.status));
      const pending = expired.filter((job) => !reissued.current.has(String(job.id)));
      if (pending.length > 0) {
        if (!(await confirmReissue(pending))) return null;
        // One at a time: an attempt made before a failure is kept, so a retry skips it.
        for (const job of pending) {
          const attempt = await reissueJob({
            id: BigInt(job.id),
            serviceReceiverId: BigInt(job.serviceReceiverId),
            serviceProviderId: BigInt(job.serviceProviderId),
          }).unwrap();
          reissued.current.set(String(job.id), attempt);
        }
      }
      const attempts = new Map(
        expired.map((job) => [String(job.id), reissued.current.get(String(job.id))!] as const),
      );
      return (id) => BigInt(attempts.get(String(id))?.id ?? id);
    },
    [confirmReissue, reissueJob],
  );

  return { reissueExpiredJobs };
}
